import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  HiOutlineBuildingOffice,
  HiOutlineUserGroup,
  HiOutlineIdentification,
  HiOutlineGlobeAlt,
  HiOutlineUsers,
  HiOutlineKey,
} from "react-icons/hi2";
import { Assessment } from "types/assessmentTypes";
import { HiOutlineLocationMarker, HiOutlineStatusOnline } from "react-icons/hi";

interface AssessmentStatsProps {
  assessment: Assessment;
  activeRespondingsCount: number;
}

export default function AssessmentStats({
  assessment,
  activeRespondingsCount,
}: AssessmentStatsProps) {
  const { t } = useTranslation();

  const company = assessment.company;

  // Stats with company details - 8 cards
  const stats = [
    {
      id: "company-info",
      icon: HiOutlineBuildingOffice,
      iconBg: "bg-brand-100 dark:bg-brand-900/30",
      iconColor: "text-brand-600 dark:text-brand-400",
      label: t("assessment.detail.stats.companyName"),
      value: company.name,
      subValue: t(`industrySectors.${company.industrySector?.key}`) || "-",
    },
    {
      id: "company-details",
      icon: HiOutlineIdentification,
      iconBg: "bg-indigo-100 dark:bg-indigo-900/30",
      iconColor: "text-indigo-600 dark:text-indigo-400",
      label: t("assessment.detail.stats.nationalId"),
      value: company.nationalId || "-",
      subValue: t(`company.activityTypes.${company.activityType}`) || "-",
    },
    {
      id: "organization",
      icon: HiOutlineGlobeAlt,
      iconBg: "bg-purple-100 dark:bg-purple-900/30",
      iconColor: "text-purple-600 dark:text-purple-400",
      label: t("assessment.detail.stats.organization"),
      value: company.organization?.name || "-",
      subValue: company.organization?.tag?.name || "-",
    },
    {
      id: "province",
      icon: HiOutlineLocationMarker,
      iconBg: "bg-cyan-100 dark:bg-cyan-900/30",
      iconColor: "text-cyan-600 dark:text-cyan-400",
      label: t("assessment.detail.stats.province"),
      value: company.province?.name || "-",
      subValue: "-",
    },
    {
      id: "staff",
      icon: HiOutlineUsers,
      iconBg: "bg-green-100 dark:bg-green-900/30",
      iconColor: "text-green-600 dark:text-green-400",
      label: t("assessment.detail.stats.staff"),
      value: `${company.totalStaffCount?.total || 0} ${t(
        `assessment.detail.stats.person`
      )}`,
      subValue: t(`company.companyTypes.${company.companyType}`) || "-",
    },
    {
      id: "user",
      icon: HiOutlineUserGroup,
      iconBg: "bg-amber-100 dark:bg-amber-900/30",
      iconColor: "text-amber-600 dark:text-amber-400",
      label: t("assessment.detail.stats.user"),
      value: company.user?.fullname || "-",
      subValue: company.user?.mobile || "-",
    },
    {
      id: "license",
      icon: HiOutlineKey,
      iconBg: "bg-navy-100 dark:bg-navy-900/30",
      iconColor: "text-navy-600 dark:text-navy-400",
      label: t("assessment.detail.stats.license"),
      value: assessment.license.licenseKey || "-",
      subValue: "-",
    },
    {
      id: "status",
      icon: HiOutlineStatusOnline,
      iconBg: "bg-teal-100 dark:bg-teal-900/30",
      iconColor: "text-teal-600 dark:text-teal-400",
      label: t("assessment.detail.stats.status"),
      value: t(`assessment.detail.status.${assessment.status}`),
      subValue: `${t(
        "assessment.detail.stats.responders"
      )}: ${activeRespondingsCount} ${t("assessment.detail.stats.person")}`,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4"
    >
      {stats.map((stat, index) => (
        <motion.div
          key={stat.id}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.05 * index }}
          className="group rounded-xl bg-white p-4 shadow-sm transition-all hover:shadow-md dark:bg-navy-800"
        >
          <div className="flex flex-col items-start gap-3 md:flex-row">
            <div className={`rounded-lg p-2.5 ${stat.iconBg}`}>
              <stat.icon className={`h-5 w-5 ${stat.iconColor}`} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-700 dark:text-gray-400">
                {stat.label}
              </p>
              <p className="font-semibold text-navy-700 dark:text-white">
                {stat.value}
              </p>
              {stat.subValue && (
                <p className="text-xs text-gray-600 dark:text-gray-500">
                  {stat.subValue}
                </p>
              )}
            </div>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
}
